import {
  Card,
  CardContent,
  CardHeader,
  Icon,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { FC, useEffect, useMemo, useRef } from 'react';
import { CirclePicker, RGBColor } from 'react-color';

export interface IProps {
  label: string;
  color: RGBColor;
  palette?: string[];
  setColor: (color: RGBColor) => void;
  children?: React.ReactNode;
}

const useStyles = () => {
  const theme = useTheme();

  return useMemo(
    () => ({
      card: {
        flex: '1 1 50%',
      },
      action: {
        margin: 'auto',
      },
      title: {
        display: 'flex',
        alignItems: 'center',
      },
      content: {
        paddingTop: theme.spacing(1),
        paddingBottom: 0,
      },
    }),
    [theme]
  );
};

/**
 * Color palette picker
 *
 * @link https://casesandberg.github.io/react-color/
 * @link https://v4.mui.com/api/card/
 */
export const ColorPalette: FC<IProps> = ({
  children,
  color,
  palette,
  label,
  setColor,
}) => {
  const styles = useStyles(); 
  const isWhite = color?.r === 255 && color?.g === 255 && color?.b === 255;
  const pickerRef = useRef<HTMLDivElement>(null);
  // Ensure palette has unique colors to prevent duplicate key errors
  const uniquePalette = useMemo(() => {
    if (!palette || palette.length === 0) {
      // Default palette if none provided
      return   [];
    }
 
    // Remove duplicates while preserving order
    const seen = new Set();
    const filtered = palette.filter((color) => {
      if (seen.has(color)) {
        console.warn(`Duplicate color found in palette: ${color}`);
        return false;
      }
      seen.add(color);
      return true;
    });

    return filtered;
  }, [palette]);
 
  useEffect(() => {
    if (!pickerRef.current) return;
    const style = () => {
      pickerRef.current?.querySelectorAll('div[title], div[style*="background"]').forEach((el: any) => {
        if (/#ffffff|rgb\(255,?\s*255,?\s*255\)|#fff/i.test((el.getAttribute('title') || '') + (el.style.backgroundColor || getComputedStyle(el).backgroundColor || ''))) {
          el.style.boxShadow = isWhite ? 'rgba(0, 0, 0, 0.12) 0 0 0 3px inset' : 'rgba(0, 0, 0, 0.12) 0 0 0 1px inset';
          el.style.transition = 'box-shadow 100ms';
        }
      });
    };
    const obs = new MutationObserver(style);
    obs.observe(pickerRef.current, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'title'] });
    const t = setTimeout(style, 50);
    return () => { obs.disconnect(); clearTimeout(t); };
  }, [isWhite]);

  const handleColorChange = (newColor: any) => {
    try {
      setColor(newColor.rgb);
    } catch (error) {
      console.error('Error setting color:', error);
    }
  };

  const handleReset = () => {
    try {
      setColor(undefined);
    } catch (error) {
      console.error('Error resetting color:', error);
    }
  };

  return (
    <Card
      sx={styles.card}
      role="region"
      aria-label={`Color palette for ${label}`}
    >
      <CardHeader
        sx={{
          action: styles.action,
          title: styles.title,
        }}
        avatar={<Typography variant="body1">{label}</Typography>}
        title={children}
        action={
          <Tooltip title="Reset color">
            <IconButton
              size="small"
              onClick={handleReset}
              aria-label={`Reset ${label} color`}
              sx={{
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.12)',
              }}
            >
             <Icon sx={{ fontSize: '0.875rem' }}>reset_alt</Icon>
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent sx={styles.content}>
      <div ref={pickerRef}>
        <CirclePicker
          color={color}
          colors={uniquePalette}
          onChangeComplete={handleColorChange}
          aria-label={`Color picker for ${label}`}
        />
        </div>
      </CardContent>
    </Card>
  );
};
